import React, { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  ExpressCheckoutElement,
  PaymentElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';

// hooks
import useAuthContext from '../../hooks/useAuthContext';
import useRecharge from '../../hooks/useRecharge';
import { PaymentIntent } from '../../services/payment/PaymentService';
import Spin from '../Common/loaders/Spin';
import PropTypes from 'prop-types';
import { useToast } from '@chakra-ui/toast';
import { useNavigate } from 'react-router';
import { BASE_URL, LOGIN } from '../../routes/app/paths';
import { classNames } from '../../utils/common';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { paymentSchema } from '../../utils/schemas';
import { encryptStorage } from '../../utils/encryptStorage';

const StripeCheckoutForm = ({
  checkout: checkoutProp,
  setSuccess,
  setError,
}) => {
  const { user, isAuthenticated } = useAuthContext();
  const queryClient = useQueryClient();
  const stripe = useStripe();
  const elements = useElements();
  const { deleteRecharge } = useRecharge();
  const toast = useToast();
  const [message, setMessage] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [checkout, setCheckout] = useState(checkoutProp);
  const [expressAvailable, setExpressAvailable] = useState(false);
  const navigate = useNavigate();
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: {
      politics: false,
    },
    resolver: yupResolver(paymentSchema),
  });

  /* Effects */
  useEffect(() => {
    if (!isAuthenticated) {
      navigate(LOGIN);
      toast({
        title: 'Debe iniciar sesión para continuar.',
        status: 'warning',
        duration: 2000,
        position: 'top-right',
        isClosable: true,
      });
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    if (!checkoutProp) return;
    setCheckout(checkoutProp);
  }, [checkoutProp]);

  useEffect(() => {
    if (!stripe) {
      return;
    }

    const clientSecret = new URLSearchParams(window.location.search).get(
      'payment_intent_client_secret',
    );

    if (!clientSecret) {
      return;
    }

    stripe.retrievePaymentIntent(clientSecret).then(({ paymentIntent }) => {
      switch (paymentIntent.status) {
        case 'succeeded':
          setMessage('Pago completado!');
          toast({
            title: 'Pago completado!',
            status: 'success',
            duration: 2000,
            position: 'top-right',
            isClosable: true,
          });
          break;
        case 'processing':
          setMessage('Su pago se esta procesando.');
          toast({
            title: 'Su pago se esta procesando.',
            status: 'info',
            duration: 2000,
            position: 'top-right',
            isClosable: true,
          });
          break;
        case 'requires_payment_method':
          setMessage('Su pago no fue completado, intente nuevamente.');
          toast({
            title: 'Su pago no fue completado, intente nuevamente.',
            status: 'error',
            duration: 2000,
            position: 'top-right',
            isClosable: true,
          });
          break;
        default:
          setMessage('Algo no ha salido bien, intente nuevamente.');
          toast({
            title: 'Algo no ha salido bien, intente nuevamente.',
            status: 'error',
            duration: 2000,
            position: 'top-right',
            isClosable: true,
          });
          break;
      }
    });
  }, [stripe]);

  const handleExpressCheckoutConfirm = async (event) => {
    if (!stripe || !elements || !checkout) {
      event.paymentFailed({ reason: 'fail' });
      return;
    }

    setIsLoading(true);

    const { error, paymentIntent } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: `${BASE_URL}/success?orderId=${checkout.id}`,
      },
      redirect: 'if_required',
    });

    if (error) {
      event.paymentFailed({ reason: 'fail' });
      setMessage(error.message || 'Error al procesar el pago.');
      setIsLoading(false);
      return;
    }

    if (paymentIntent?.status === 'succeeded') {
      try {
        const fetchPay = await queryClient.fetchQuery(
          ['rechargeDo'],
          () => PaymentIntent({
            token: user.access_token,
            id: checkout.id,
            pay_method: 'stripe',
            stripe_payment_id: paymentIntent.id,
          }),
        );

        if (fetchPay.status === 201) {
          const { paymentIntent: pi, error: piError } = await stripe.confirmCardPayment(fetchPay.data.data);
          queryClient.refetchQueries(['contactsRechargesByUser']);
          queryClient.refetchQueries(['userPoints']);
          if (piError) {
            event.paymentFailed({ reason: 'fail' });
            setError(true);
          } else if (pi.status === 'succeeded') {
            deleteRecharge();
            setSuccess(true);
            encryptStorage.setItem('checkout_id', checkout.id);
          }
        } else if (fetchPay.status === 200 || fetchPay.status === 204) {
          deleteRecharge();
          queryClient.refetchQueries(['contactsRechargesByUser']);
          queryClient.refetchQueries(['userPoints']);
          setSuccess(true);
          encryptStorage.setItem('checkout_id', checkout.id);
        }
      } catch (err) {
        event.paymentFailed({ reason: 'fail' });
        setError(true);
        encryptStorage.removeItem('checkout_id');
      }
    }

    setIsLoading(false);
  };

  const onSubmit = async () => {
    if (!stripe || !elements) {
      // Stripe.js has not yet loaded.
      // Make sure to disable form submission until Stripe.js has loaded.
      return;
    }

    setIsLoading(true);

    const { error, setupIntent } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: `${BASE_URL}/success?orderId=${checkout.id}`,
      }
    });

    if (setupIntent) {
      // const payment_method = setupIntent.payment_method;

      try {
        const fetchPay = await queryClient.fetchQuery(
          ['rechargeDo'],
          () => PaymentIntent({
            token: user.access_token,
            id: checkout.id,
            pay_method: 'stripe',
            stripe_payment_id: setupIntent.id,
          },),
        );

        if (fetchPay.status === 201) {
          const { paymentIntent, error } = await stripe.confirmCardPayment(
            fetchPay.data.data,
          );
          // reload history recharge, in Profile
          queryClient.refetchQueries(['contactsRechargesByUser']);
          queryClient.refetchQueries(['userPoints']);

          if (error) {
            setError(true);
          } else if (paymentIntent.status == 'succeeded') {
            deleteRecharge();
            setSuccess(true);

            // save in localStorage the checkout id for return_url
            encryptStorage.setItem('checkout_id', checkout.id);
          }
        } else if (fetchPay.status === 200 || fetchPay.status === 204) {
          deleteRecharge();
          queryClient.refetchQueries(['contactsRechargesByUser']);
          queryClient.refetchQueries(['userPoints']);
          setSuccess(true);

          // save in localStorage the checkout id for return_url
          encryptStorage.setItem('checkout_id', checkout.id);
        }
      } catch (error) {
        setError(true);
        encryptStorage.removeItem('checkout_id');
      }
    } else if (error) {
      if (error.type === 'card_error' || error.type === 'validation_error') {
        setMessage(error.message);
      } else {
        setMessage('An unexpected error occurred.');
      }
      encryptStorage.removeItem('checkout_id');
    }
    setIsLoading(false);
  };

  return (
    <form id="payment-form" onSubmit={handleSubmit(onSubmit)} className='max-w-xs min-w-[250px]'>
      {/* Apple Pay / Google Pay express checkout */}
      <ExpressCheckoutElement
        onConfirm={handleExpressCheckoutConfirm}
        onReady={({ availablePaymentMethods }) => {
          setExpressAvailable(!!availablePaymentMethods);
        }}
        options={{
          wallets: { applePay: 'always', googlePay: 'always' },
          buttonHeight: 48,
        }}
      />

      {expressAvailable && (
        <div className="flex items-center gap-2 my-4">
          <hr className="flex-1 border-gray-200" />
          <span className="text-gray-400 text-xs">o paga con tarjeta</span>
          <hr className="flex-1 border-gray-200" />
        </div>
      )}

      {/* Stripe form */}
      <PaymentElement id="payment-element" />

      {/* Politics */}
      <div className="relative flex items-start mt-4 w-fit">
        <div className="flex h-6 items-center">
          <input
            id="poltics"
            aria-describedby="politics"
            name="poltic"
            type="checkbox"
            className={classNames(
              'h-4 w-4 rounded-full text-bubble-gum focus:ring-bubble-gum focus:ring-offset-0 focus:ring-opacity-1 focus:border-0',
              errors.politics ? 'border-2 border-red-500' : 'border-gray-400',
            )}
            {...register('politics', { required: true })}
          />
        </div>
        <div className="ml-3 text-left leading-6">
          <label
            htmlFor="poltics"
            className="font-light text-gray-900 text-[12px] md:text-sm leading-3"
          >
            Acepto los{' '}
            <span className="text-bubble-gum font-semibold">
              Términos y condiciones
            </span>{' '}
            y confirmo que tengo pleno conocimiento de cómo se usan mis datos de
            acuerdo a la{' '}
            <span className="text-bubble-gum font-semibold">
              política de privacidad
            </span>
            .
          </label>{' '}
        </div>
      </div>
      {errors.politics && (
        <span className="text-bubble-gum text-xs">
          {errors.politics.message}
        </span>
      )}

      {/* Pay Button */}
      <div className="flex w-full justify-center mt-12 select-none">
        <button
          disabled={isLoading || !stripe || !elements || !checkout || errors.politics}
          id="submit"
          type="submit"
          className={classNames(
            isLoading || !stripe || !elements || !checkout || errors.politics
              ? 'bg-gray-300'
              : 'bg-green-secondary hover:shadow-md active:bg-green-secondary/80',
            'p-2 px-16 text-lg rounded-full text-blue-primary disable:bg-gray-300 flex justify-center',
          )}
        >
          {isLoading ? <Spin /> : !checkout ? 'Cargando...' : 'Pagar'}
        </button>
      </div>
      {/* Show any error or success messages */}
      {message && (
        <div className="text-sm text-bubble-gum text-center mt-3">
          {message}
        </div>
      )}
    </form>
  );
};

StripeCheckoutForm.propTypes = {
  checkout: PropTypes.shape({
    id: PropTypes.number.isRequired, // al parecer, solo se usa "id" del checkout, lo demas sobra
    type: PropTypes.string.isRequired,
    rechargeable_id: PropTypes.number.isRequired,
    rechargeable_type: PropTypes.string.isRequired,
    rechargeable_object: PropTypes.shape({
      id: PropTypes.number,
      dtone_product_id: PropTypes.string,
      name: PropTypes.string,
      description: PropTypes.string,
      type: PropTypes.string,
      provider_price_usd: PropTypes.number,
      provider_price_eur: PropTypes.number,
      price_usd: PropTypes.number,
      price_eur: PropTypes.number,
      scheduled_price_usd: PropTypes.number,
      scheduled_price_eur: PropTypes.number,
      offer_price_usd: PropTypes.number,
      offer_price_eur: PropTypes.number,
      assign_points: PropTypes.number,
      offer_assign_points: PropTypes.number,
      cost_points: PropTypes.number,
      status: PropTypes.bool,
      created_at: PropTypes.string,
      updated_at: PropTypes.string,
    }).isRequired,
    user_id: PropTypes.number.isRequired,
    user: PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
      last_name: PropTypes.string,
      secound_last_name: PropTypes.string,
      email: PropTypes.string,
      email_verified_at: PropTypes.string,
      role: PropTypes.string,
      ads: PropTypes.number,
      point: PropTypes.string,
      birthday: PropTypes.string,
      phone: PropTypes.string,
      adress: PropTypes.string,
      avatar: PropTypes.string,
      group_id: PropTypes.number,
      created_at: PropTypes.string,
      updated_at: PropTypes.string,
      stripe_id: PropTypes.string,
    }).isRequired,
  }).isRequired,
  setSuccess: PropTypes.func.isRequired,
  setError: PropTypes.func.isRequired,
};

export default StripeCheckoutForm;
